import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Search, Star, Zap, Clock, LayoutGrid, Car, Watch, Banknote } from "lucide-react";
import api from "../lib/api";
import { SectionTitle, Spinner, EmptyState } from "../components/Neon";
import { CompetitionCard } from "../components/CompetitionCard";
import { timeParts } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TABS = [
  { key: "all", label: "All", icon: LayoutGrid },
  { key: "featured", label: "Featured", icon: Star },
  { key: "instant", label: "Instant Wins", icon: Zap },
  { key: "members", label: "Members", icon: Star },
  { key: "ending", label: "Ending Soon", icon: Clock },
];

// Brand category landing entry points. Matches the backend `category` field.
const CATEGORIES = [
  { key: "Cars", label: "Cars", icon: Car },
  { key: "Watches", label: "Watches", icon: Watch },
  { key: "Cash", label: "Cash", icon: Banknote },
];

export default function Competitions() {
  const { category: routeCategory } = useParams();
  const [searchParams] = useSearchParams();
  const activeCategory = routeCategory || searchParams.get("category") || "";
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("featured");
  const [tab, setTab] = useState("all");

  useEffect(() => {
    setLoading(true);
    const url = activeCategory ? `/competitions?category=${encodeURIComponent(activeCategory)}` : "/competitions";
    api.get(url).then(({ data }) => setComps(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [activeCategory]);

  const filtered = useMemo(() => {
    let list = comps.filter((c) => c.title.toLowerCase().includes(q.toLowerCase()) || (c.metadata_tags || []).join(" ").toLowerCase().includes(q.toLowerCase()));
    // Defensive client-side category filter (in case the backend ignores the query param).
    if (activeCategory) list = list.filter((c) => (c.category || "").toLowerCase() === activeCategory.toLowerCase());
    if (tab === "featured") list = list.filter((c) => c.featured);
    if (tab === "instant") list = list.filter((c) => c.instant_wins_enabled);
    if (tab === "members") list = list.filter((c) => c.subscriber_eligible);
    if (tab === "ending") list = list.filter((c) => { const t = timeParts(c.ends_at); return !t.done && t.d < 3; });
    if (sort === "featured") list = [...list].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    if (sort === "price-low") list = [...list].sort((a, b) => a.ticket_price - b.ticket_price);
    if (sort === "price-high") list = [...list].sort((a, b) => b.ticket_price - a.ticket_price);
    if (sort === "ending") list = [...list].sort((a, b) => new Date(a.ends_at) - new Date(b.ends_at));
    if (sort === "prize") list = [...list].sort((a, b) => b.prize_value - a.prize_value);
    return list;
  }, [comps, q, sort, tab, activeCategory]);

  const countFor = (key) => {
    if (key === "all") return comps.length;
    if (key === "featured") return comps.filter((c) => c.featured).length;
    if (key === "instant") return comps.filter((c) => c.instant_wins_enabled).length;
    if (key === "members") return comps.filter((c) => c.subscriber_eligible).length;
    if (key === "ending") return comps.filter((c) => { const t = timeParts(c.ends_at); return !t.done && t.d < 3; }).length;
    return 0;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <SectionTitle accent="white" sub={activeCategory ? `Live ${activeCategory} competitions — answer the skill question to enter.` : "Every live competition. Answer the skill question to enter."}>
        {activeCategory ? `${activeCategory} Competitions` : "All Competitions"}
      </SectionTitle>

      {/* Category landing chips */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="category-chips">
        <Link to="/competitions" data-testid="category-chip-all"
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-display uppercase tracking-wider transition-colors ${!activeCategory ? "bg-[var(--neon-cyan)] text-black" : "border border-white/15 text-[var(--text-muted)] hover:text-white hover:border-white/30"}`}>
          <LayoutGrid className="h-4 w-4" /> All Prizes
        </Link>
        {CATEGORIES.map((cat) => (
          <Link key={cat.key} to={`/category/${cat.key}`} data-testid={`category-chip-${cat.key.toLowerCase()}`}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-display uppercase tracking-wider transition-colors ${activeCategory.toLowerCase() === cat.key.toLowerCase() ? "bg-[var(--neon-cyan)] text-black" : "border border-white/15 text-[var(--text-muted)] hover:text-white hover:border-white/30"}`}>
            <cat.icon className="h-4 w-4" /> {cat.label}
          </Link>
        ))}
      </div>

      {/* Tabs */}
      <div className="mt-4 flex gap-2 overflow-x-auto pb-1" data-testid="competitions-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`competitions-tab-${t.key}`}
            className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-display uppercase tracking-wider transition-colors ${tab === t.key ? "bg-[var(--neon-pink)] text-black" : "border border-white/15 text-[var(--text-muted)] hover:text-white hover:border-white/30"}`}>
            <t.icon className="h-4 w-4" /> {t.label} <span className={`text-[11px] ${tab === t.key ? "opacity-80" : "opacity-60"}`}>({countFor(t.key)})</span>
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-muted)]" />
          <input data-testid="competitions-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search prizes…" className="w-full bg-[var(--surface)] border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--neon-cyan)]" />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="competitions-sort" className="w-full sm:w-52 bg-[var(--surface)] border-white/10 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="featured">Featured first</SelectItem>
            <SelectItem value="ending">Ending soon</SelectItem>
            <SelectItem value="prize">Biggest prize</SelectItem>
            <SelectItem value="price-low">Price: low to high</SelectItem>
            <SelectItem value="price-high">Price: high to low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <Spinner /> : filtered.length === 0 ? (
        <div className="mt-8"><EmptyState title="No competitions found" subtitle={tab === "featured" ? "No featured competitions right now — check back soon." : "Try a different search or tab."} /></div>
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {filtered.map((c) => <CompetitionCard key={c.id} comp={c} />)}
        </div>
      )}
    </div>
  );
}
